import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Sidebar from "../../components/sidebar/Sidebar";
import PageContainer from "../../components/layout/PageContainer";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { useUserRole } from "../../hooks/useUserRole";
import {
  useGetRecommendationsQuery,
  useAcceptRecommendationMutation,
  useRejectRecommendationMutation,
  useRetryApplyMutation,
  useGenerateRecommendationsMutation,
} from "../../store/apis/controlApi";
import { ApiResponse, Recommendation } from "../../types/control";
import { formatCurrency } from "../../utils/formatUtils";
import RecommendationCard from "../../components/control/RecommendationCard";
import RecommendationActions from "../../components/control/RecommendationActions";
import ControlSettingsModal from "../../components/control/ControlSettingsModal";
import {
  ApplyStatusChip,
  directionColor,
} from "../../components/control/controlPresentation";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";

type ViewMode = "cards" | "table";

const Control: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const collapsed = useSelector(selectSidebarCollapsed);
  const { role, isAdmin, isStrategy } = useUserRole();

  const canConfigure = isAdmin || isStrategy;

  const [view, setView] = useState<ViewMode>("cards");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const {
    data: resp,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetRecommendationsQuery({ status: "PENDING", pageSize: 500 });

  const [acceptRecommendation] = useAcceptRecommendationMutation();
  const [rejectRecommendation] = useRejectRecommendationMutation();
  const [retryApply] = useRetryApplyMutation();
  const [generate, { isLoading: isGenerating }] =
    useGenerateRecommendationsMutation();

  // El guard de rol va después de los hooks (reglas de hooks de React).
  if (role && !["ADMIN", "STRATEGY", "TECHNICIAN", "PRICING"].includes(role)) {
    return <Navigate to="/" replace />;
  }

  const items = resp?.data?.items ?? [];

  const is409 = (err: unknown) =>
    typeof err === "object" &&
    err !== null &&
    (err as { status?: number }).status === 409;

  // Feedback según el applyStatus que devuelve aceptar/reintentar.
  const reportApplyResult = (rec?: Recommendation) => {
    if (!rec) return;
    if (rec.applyStatus === "APPLIED") {
      dispatch(
        setToastMessage({
          message: t("control.toasts.applied"),
          severity: "success",
        })
      );
    } else if (rec.applyStatus === "FAILED") {
      dispatch(
        setToastMessage({
          message: rec.applyError || t("control.toasts.applyFailed"),
          severity: "error",
        })
      );
    } else if (rec.applyStatus === "MANUAL_REQUIRED") {
      dispatch(
        setToastMessage({
          message: t("control.toasts.manualRequired"),
          severity: "info",
        })
      );
    } else {
      dispatch(
        setToastMessage({
          message: t("control.toasts.accepted"),
          severity: "success",
        })
      );
    }
  };

  const handleConflict = () => {
    dispatch(
      setToastMessage({
        message: t("control.toasts.conflict"),
        severity: "warning",
      })
    );
    refetch();
  };

  const handleAccept = async (id: string, note?: string) => {
    setBusyId(id);
    try {
      const res = (await acceptRecommendation({
        id,
        note,
      }).unwrap()) as ApiResponse<Recommendation>;
      reportApplyResult(res.data);
    } catch (err) {
      if (is409(err)) handleConflict();
      else
        dispatch(
          setToastMessage({
            message:
              (err as { data?: { message?: string } })?.data?.message ||
              t("control.toasts.acceptError"),
            severity: "error",
          })
        );
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id: string, note?: string) => {
    setBusyId(id);
    try {
      await rejectRecommendation({ id, note }).unwrap();
      dispatch(
        setToastMessage({
          message: t("control.toasts.denied"),
          severity: "success",
        })
      );
    } catch (err) {
      if (is409(err)) handleConflict();
      else
        dispatch(
          setToastMessage({
            message:
              (err as { data?: { message?: string } })?.data?.message ||
              t("control.toasts.denyError"),
            severity: "error",
          })
        );
    } finally {
      setBusyId(null);
    }
  };

  const handleRetry = async (id: string) => {
    setBusyId(id);
    try {
      const res = (await retryApply(
        id
      ).unwrap()) as ApiResponse<Recommendation>;
      reportApplyResult(res.data);
    } catch (err) {
      dispatch(
        setToastMessage({
          message:
            (err as { data?: { message?: string } })?.data?.message ||
            t("control.toasts.retryError"),
          severity: "error",
        })
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleGenerate = async () => {
    try {
      const res = await generate().unwrap();
      const g = res.data;
      dispatch(
        setToastMessage({
          message: t("control.toasts.generateOk", {
            created: g?.created ?? 0,
            refreshed: g?.refreshed ?? 0,
            expired: g?.expired ?? 0,
          }),
          severity: "success",
        })
      );
    } catch (err) {
      if (is409(err))
        dispatch(
          setToastMessage({
            message: t("control.toasts.generateInProgress"),
            severity: "warning",
          })
        );
      else
        dispatch(
          setToastMessage({
            message:
              (err as { data?: { message?: string } })?.data?.message ||
              t("control.toasts.generateError"),
            severity: "error",
          })
        );
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="h3">{t("control.title")}</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <ToggleButtonGroup
                value={view}
                exclusive
                size="small"
                onChange={(_e, v: ViewMode | null) => v && setView(v)}
              >
                <ToggleButton value="cards">
                  <Tooltip title={t("control.viewCards")}>
                    <ViewModuleIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="table">
                  <Tooltip title={t("control.viewTable")}>
                    <ViewListIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => navigate("/control/historial")}
                sx={{ textTransform: "none" }}
              >
                {t("control.history")}
              </Button>
              {canConfigure && (
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={() => setSettingsOpen(true)}
                  sx={{ textTransform: "none" }}
                >
                  {t("control.settings")}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={
                  isGenerating ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={handleGenerate}
                disabled={isGenerating}
                sx={{ textTransform: "none" }}
              >
                {t("control.regenerate")}
              </Button>
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error" sx={{ mb: 2 }}>
                {t("control.loadError")}
              </Typography>
              <Button variant="outlined" onClick={() => refetch()}>
                {t("control.retry")}
              </Button>
            </Paper>
          ) : items.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: "center" }}>
              <Typography variant="h6" color={theme.palette.neutral[600]}>
                {t("control.empty")}
              </Typography>
            </Paper>
          ) : view === "cards" ? (
            <Stack spacing={2} sx={{ opacity: isFetching ? 0.6 : 1 }}>
              {items.map((r) => (
                <RecommendationCard
                  key={r.id}
                  recommendation={r}
                  busy={busyId === r.id}
                  onAccept={(note) => handleAccept(r.id, note)}
                  onReject={(note) => handleReject(r.id, note)}
                  onRetry={() => handleRetry(r.id)}
                />
              ))}
            </Stack>
          ) : (
            <Paper sx={{ opacity: isFetching ? 0.6 : 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          fontWeight: 700,
                          bgcolor: theme.palette.neutral["100"],
                        },
                      }}
                    >
                      <TableCell>{t("control.table.line")}</TableCell>
                      <TableCell>{t("control.table.ioClient")}</TableCell>
                      <TableCell>{t("control.table.direction")}</TableCell>
                      <TableCell align="right">
                        {t("control.table.current")}
                      </TableCell>
                      <TableCell align="right">
                        {t("control.table.suggested")}
                      </TableCell>
                      <TableCell align="right">
                        {t("control.table.variation")}
                      </TableCell>
                      <TableCell>{t("control.table.applyStatus")}</TableCell>
                      <TableCell align="right">
                        {t("control.table.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Typography variant="body2">{r.lineName}</Typography>
                          <Typography
                            variant="caption"
                            color={theme.palette.neutral[600]}
                          >
                            {r.sourceId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {r.insertionOrderName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={theme.palette.neutral[600]}
                          >
                            {r.companyName}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            color: directionColor(theme)[r.direction],
                            fontWeight: 700,
                          }}
                        >
                          {t(`control.direction.${r.direction}`)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(r.currentInvestment, r.currency)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(r.suggestedInvestment, r.currency)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: directionColor(theme)[r.direction],
                            fontWeight: 700,
                          }}
                        >
                          {r.variationPercent > 0 ? "+" : ""}
                          {r.variationPercent}%
                        </TableCell>
                        <TableCell>
                          <ApplyStatusChip applyStatus={r.applyStatus} />
                        </TableCell>
                        <TableCell align="right">
                          <RecommendationActions
                            recommendation={r}
                            busy={busyId === r.id}
                            size="small"
                            onAccept={(note) => handleAccept(r.id, note)}
                            onReject={(note) => handleReject(r.id, note)}
                            onRetry={() => handleRetry(r.id)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </PageContainer>
      </Box>

      {canConfigure && (
        <ControlSettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </Box>
  );
};

export default Control;
